import type { CityConfig } from "@/pages/city-template/types";

export const hoodRiverConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Hood River",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "hood-river",
  route: "/hood-river",
  title: "Emergency Passport Hood River OR | Expedited Passport Columbia River Gorge",
  metaDescription: "Emergency passport and expedited passport renewal in Hood River, OR — as fast as 24 hours. Fast Passport Center serves Hood River and the Columbia River Gorge. Registered State Dept. courier. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Hood River, Oregon",
  heroSubtitle: "Windsurfing trip to Baja turns into a real trip? International wine tour booked last minute? Passport expired before your Columbia Gorge adventure extends abroad? Fast Passport Center helps Hood River residents get their expedited passport fast — as fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Hood%20River%20Oregon%20town%20nestled%20dramatically%20between%20Mount%20Hood%20snowcapped%20volcano%20and%20the%20wide%20Columbia%20River%20Gorge%2C%20lush%20Columbia%20River%20valley%20with%20sailboard%20in%20the%20water%2C%20golden%20afternoon%20light%20illuminating%20orchards%20and%20basalt%20cliffs%2C%20dramatic%20Pacific%20Northwest%20landscape%20photography%20with%20rich%20warm%20tones%2C%20vibrant%20and%20photorealistic%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=hood-river-or-hero-01&orientation=landscape",
  heroImageAlt: "Hood River Oregon with Mount Hood and Columbia River Gorge with sailboard on the river",
  geoPosition: "45.7054;-121.5219",
  geoPlacename: "Hood River, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 195 miles north — 3.5 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Hood River", zips: "97031" },
    { name: "Cascade Locks", zips: "97014" },
    { name: "White Salmon", zips: "98672" },
    { name: "Bingen", zips: "98605" },
    { name: "The Dalles", zips: "97058" },
    { name: "Stevenson", zips: "98648" },
    { name: "Mosier", zips: "97040" },
    { name: "Gresham", zips: "97030, 97080" },
    { name: "Troutdale", zips: "97060" },
    { name: "Parkdale", zips: "97041" },
    { name: "Portland", zips: "97201–97299" },
    { name: "Sandy", zips: "97055" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Hood River", desc: "Complete your emergency or expedited passport order from Hood River — no Portland office visit required to start." },
    { icon: "ri-truck-line", title: "Ship Overnight from Hood River or The Dalles", desc: "UPS and FedEx options in Hood River and The Dalles serve the entire Columbia Gorge corridor for overnight passport shipping." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission — essential for Hood River's international adventure travel community with complex itineraries." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We bypass the public appointment system with reserved Seattle Passport Agency slots — no 3.5-hour drive required." },
  ],
  testimonials: [
    {
      name: "Tyler B.",
      location: "Hood River, OR",
      rating: 5,
      text: "Kite-surfing trip to Baja California booked spontaneously — my passport had expired two years ago. Fast Passport Center was unreal. Shipped from The Dalles FedEx and had my renewal in 6 days. Made the trip with three days to spare.",
      service: "Emergency Passport",
    },
    {
      name: "Sarah N.",
      location: "Hood River, OR",
      rating: 5,
      text: "Wine tour trip to the Burgundy region of France — I discovered my passport was expiring one month before the trip. Fast Passport Center renewed it in 8 days. Excellent service and clear communication.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Mark W.",
      location: "White Salmon, WA",
      rating: 5,
      text: "Our family lives across the Gorge in White Salmon and needed passports for a Canadian road trip. Fast Passport Center handled everything — shipped from Hood River UPS Store, passports arrived in 9 days for the whole family.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "hood-river-post",
      name: "Hood River Post Office",
      type: "USPS Passport Acceptance",
      address: "408 Cascade Ave",
      city: "Hood River",
      state: "OR",
      zip: "97031",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Downtown Hood River — primary local acceptance location",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility in Hood River. Appointment required — book well in advance, especially during the Gorge's busy spring and summer windsports seasons.",
      icon: "ri-mail-line",
    },
    {
      id: "cascade-locks-post",
      name: "Cascade Locks Post Office",
      type: "USPS Passport Acceptance",
      address: "200 NW Herman Creek Rd",
      city: "Cascade Locks",
      state: "OR",
      zip: "97014",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Cascade Locks — about 22 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Small post office — very limited appointment availability. Hood River Post Office preferred.",
      icon: "ri-mail-line",
    },
    {
      id: "the-dalles-post",
      name: "The Dalles Post Office",
      type: "USPS Passport Acceptance",
      address: "1001 E 2nd St",
      city: "The Dalles",
      state: "OR",
      zip: "97058",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "The Dalles — about 20 miles east, larger office",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Larger The Dalles post office with better appointment availability and photo services for Hood River-area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "white-salmon-post",
      name: "White Salmon Post Office",
      type: "USPS Passport Acceptance",
      address: "189 E Jewett Blvd",
      city: "White Salmon",
      state: "WA",
      zip: "98672",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "White Salmon, WA — across the Columbia, about 5 miles",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "For Hood River Gorge community members living across the river in White Salmon, WA — also a passport acceptance location.",
      icon: "ri-mail-line",
    },
    {
      id: "gresham-post",
      name: "Gresham Post Office",
      type: "USPS Passport Acceptance",
      address: "600 NW Division St",
      city: "Gresham",
      state: "OR",
      zip: "97030",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Gresham — about 55 miles west, large office",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Gresham post office — significantly larger, with better appointment availability for Hood River residents who commute to the Portland area.",
      icon: "ri-mail-line",
    },
    {
      id: "mosier-post",
      name: "Mosier Post Office",
      type: "USPS Passport Acceptance",
      address: "100 Front Ave",
      city: "Mosier",
      state: "OR",
      zip: "97040",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Mosier — about 8 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Very small rural post office — minimal appointment availability. Hood River or The Dalles strongly preferred.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Hood River, Oregon?",
      a: "Yes. Through Fast Passport Center, Hood River residents can get an emergency passport in as fast as 24 hours for urgent travel, or 2–9 business days with expedited processing. Start online, ship overnight from Hood River or The Dalles FedEx or UPS, and we submit through reserved Seattle agency channels. No office visit required.",
    },
    {
      q: "How fast can I renew my passport from Hood River?",
      a: "Expedited passport renewal from Hood River is available through Fast Passport Center in as little as 3 business days. Most adult renewals do not require an in-person acceptance visit. Ship overnight from the Hood River UPS Store or The Dalles FedEx location — we handle the agency submission.",
    },
    {
      q: "Does Hood River have a passport agency?",
      a: "Hood River does not have a regional passport agency. The nearest is the Seattle Passport Agency, about 195 miles north (3.5-hour drive). For Hood River-area residents, Fast Passport Center is the faster and more practical option — start online, ship overnight, no Seattle drive required.",
    },
    {
      q: "Where do Hood River residents go for passport acceptance?",
      a: "The Hood River Post Office (408 Cascade Ave) is the primary local acceptance location. The Dalles Post Office is 20 miles east and has better appointment availability. For Gorge community members living in White Salmon, WA, the White Salmon Post Office also accepts passports.",
    },
    {
      q: "Can I expedite a passport for a last-minute Baja or Mexico trip from Hood River?",
      a: "Yes. Hood River's outdoor sports culture generates a lot of last-minute Mexico trip passport urgencies — kite-surfing and windsurfing trips to Baja, surf trips to Oaxaca, and winter beach vacations. Fast Passport Center handles these with expedited processing as fast as 3 business days for renewal-eligible applicants.",
    },
    {
      q: "Do I need a passport for a Hood River to Canada road trip?",
      a: "A U.S. passport is required for any air travel between the U.S. and Canada. For land crossings, NEXUS cards and enhanced driver licenses (WA state residents only) are alternatives — but Oregon residents cannot get an enhanced license. If you're crossing into BC for windsurfing, skiing, or hiking, a passport is your best bet.",
    },
    {
      q: "How long does standard passport processing take from Hood River?",
      a: "Standard USPS passport processing out of Hood River runs 6–13 weeks everywhere — and during peak spring/summer Gorge season it can stretch to 14+ weeks. If your trip is in under 10 weeks, you need Fast Passport Center's expedited service.",
    },
    {
      q: "What airport do Hood River residents use for international travel?",
      a: "Portland International Airport (PDX) is the primary international departure point for Hood River travelers, about 60 miles west via I-84. PDX offers direct international service to Amsterdam, London, Reykjavik, Tokyo, Vancouver BC, and Mexico City. A valid passport is required for all international departures.",
    },
    {
      q: "Can I renew a passport from Hood River without visiting an office?",
      a: "Yes. Most adult Hood River residents renewing a passport can do so entirely by mail — no in-person visit required. Start online with Fast Passport Center, complete your documents at home, and ship overnight from the Hood River UPS Store or The Dalles FedEx. No office visit to our facility required.",
    },
    {
      q: "Can Hood River residents across the river in White Salmon, WA also use Fast Passport Center?",
      a: "Yes. Fast Passport Center serves the entire Columbia River Gorge community — both Oregon and Washington sides. White Salmon, Bingen, and Stevenson residents can use the same process: start online, ship overnight from Hood River or White Salmon UPS/FedEx locations, and we handle the rest.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Hood River", address: "1205 12th St, Hood River, OR 97031", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/" },
    { name: "The UPS Store — Hood River", address: "1625 12th St, Hood River, OR 97031", hours: "Mon–Fri 8 AM – 7 PM, Sat 9 AM – 5 PM", website: "https://www.theupsstore.com/" },
    { name: "Walgreens — The Dalles", address: "2802 W 2nd St, The Dalles, OR 97058", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/" },
    { name: "Walmart — The Dalles", address: "3200 W 6th St, The Dalles, OR 97058", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/" },
    { name: "FedEx Office — Gresham", address: "1501 NE Halsey St, Gresham, OR 97230", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM", website: "https://www.fedex.com/" },
    { name: "Walgreens — Gresham", address: "1501 NW Burnside Rd, Gresham, OR 97030", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/" },
    { name: "CVS — Troutdale", address: "23715 NE Sandy Blvd, Troutdale, OR 97060", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/" },
    { name: "Walmart — Troutdale area", address: "2500 SE 1st St, Gresham, OR 97080", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/" },
    { name: "FedEx Office — Portland SE", address: "810 SW Broadway, Portland, OR 97205", hours: "Mon–Fri 7 AM – 10 PM", website: "https://www.fedex.com/" },
    { name: "Walgreens — Sandy", address: "37617 Hwy 26, Sandy, OR 97055", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/" },
  ],
  dmvLocations: [
    {
      name: "Hood River Driver & Motor Vehicle Services",
      address: "3223 Avalon Dr, Hood River, OR 97031",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Hood River DMV — driver licenses, REAL ID, and ID cards for Hood River County residents.",
      featured: true,
    },
    {
      name: "The Dalles Driver & Motor Vehicle Services",
      address: "706 E 2nd St, The Dalles, OR 97058",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "The Dalles DMV — alternative for Hood River area residents in eastern Gorge communities.",
      featured: false,
    },
    {
      name: "Gresham Driver & Motor Vehicle Services",
      address: "285 W Powell Blvd, Gresham, OR 97030",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Gresham DMV — western option for Hood River residents commuting toward Portland.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — about 60 miles west via I-84",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX is the main international departure point for Hood River travelers, offering direct service to Amsterdam, London, Reykjavik, Frankfurt, Tokyo, Vancouver BC, and major U.S. hubs. Allow extra travel time from Hood River during peak Gorge season. A valid U.S. passport is required for all international PDX departures.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 195 miles north via I-84 and I-5",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some Hood River area travelers use SEA for additional international flight options or Alaska cruise departures. Passport required for all international departures.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Hood River & Columbia Gorge",
      description: "Spontaneous Baja kiteboarding trip? International wine tour confirmed last week? Emergency abroad? Fast Passport Center handles emergency passport processing for Hood River and the Columbia Gorge community — as fast as 24 hours.",
      bullets: ["As fast as 24 hours processing", "Reserved Seattle agency submission", "Dedicated agent and live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired during the pandemic? Hood River residents renew fast through Fast Passport Center — ship from local UPS or The Dalles FedEx overnight, we handle the expedited agency submission.",
      bullets: ["Fastest renewal option for Hood River residents", "Ship from Hood River or The Dalles overnight", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport in the Gorge or before an international trip? We guide Hood River travelers through DS-64 and expedited replacement — urgency handled, no Portland run required.",
      bullets: ["DS-64 reporting guidance", "Expedited replacement timelines available", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Gorge Families",
      description: "Hood River families traveling to British Columbia, Mexico, or internationally with children need child passports. We provide the checklist for the Hood River Post Office acceptance step — you handle the in-person visit, we expedite from there.",
      bullets: ["Both parents typically required to consent", "Hood River Post Office guidance", "Expedited after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First-time passport from Hood River? We guide you through the acceptance clerk step at the Hood River or The Dalles Post Office and handle expedited processing after.",
      bullets: ["Step-by-step acceptance guidance", "Photo location guide for Hood River area", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels at prices 30–100% lower than FedEx Office, Staples, and other resellers.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Passport Services",
      description: "Name change after marriage, damaged passport replacement, second passports for Hood River's frequent international travelers and outdoor sports professionals — all with expedited courier access.",
      bullets: ["Name change passports", "Damaged passport replacement", "Second passport for business and sports travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Hood River, Oregon travelers with urgent international departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Hood River and Columbia River Gorge residents needing passports faster than standard government timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Hood River, Oregon residents — ship overnight from Hood River or The Dalles, we handle the rest." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Hood River travelers with lost or stolen passports before travel." },
    { name: "Child Passport", description: "Expedited child passport services for Hood River and Columbia River Gorge families heading to British Columbia, Mexico, or international destinations." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission serving Hood River, Oregon and the Columbia River Gorge corridor." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Booking Adventure Travel Without Checking Passport Validity", description: "Hood River's outdoor sports culture drives a lot of spontaneous international trip bookings — kite trips to Baja, ski trips to Whistler, surf trips to Costa Rica. Many Gorge residents book first and check their passport after. Check the expiration date BEFORE booking." },
    { icon: "ri-camera-off-line", title: "Wrong or Rejected Passport Photo", description: "Photos from drugstores frequently get rejected for lighting or expression issues. Fast Passport Center reviews photo specs before submission to prevent this costly delay." },
    { icon: "ri-file-damage-line", title: "Incomplete or Missing Signatures on Forms", description: "One missing signature or incorrect date and your application comes back — costing you weeks. Our pre-submission review catches these before submission." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Format", description: "Some applicants have only short-form birth certificates, which most states don't accept for passports. We specify exactly what you need based on where you were born." },
    { icon: "ri-calendar-close-line", title: "Waiting Until the Last Windsports Season to Renew", description: "Every April and May, Hood River Gorge residents start planning international wind and kite-surfing trips — often discovering their passport expired during winter. Don't wait for the season. Renew now." },
    { icon: "ri-map-pin-time-line", title: "Ignoring the Six-Month Validity Rule", description: "Mexico, Canada (by air), and most of Europe require your passport to be valid for six months beyond your return date. A passport expiring in three months is effectively expired for most international travel." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Hood River travel date. Complete your secure order from home in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized document checklist — exactly what to gather, with Hood River-specific acceptance location recommendations." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Hood River or The Dalles", description: "Drop off at the Hood River UPS Store or The Dalles FedEx — both open evenings for Columbia Gorge residents." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Seattle Passport Agency slots — no waiting for public appointment cancellations or driving 3.5 hours." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped Directly to Hood River", description: "Your completed passport ships directly to your Hood River or Gorge-area address. Track status in real time." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 3.5 hrs each way",
        "Life-or-death emergencies only",
        "Very limited availability",
        "Not practical for most Gorge residents",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship from Hood River or The Dalles",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks; 14+ in peak Gorge season",
        "In-person clerk appointment required",
        "No document review",
        "No dedicated agent",
        "Cannot upgrade after submission",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited, weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3.5 hours each way from Hood River)", fpc: "Ship overnight from Hood River or The Dalles" },
    { feature: "Processing Speed", agency: "Strict eligibility — urgent travel only", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Columbia Gorge Accessibility", agency: "Not designed for rural Gorge residents", fpc: "Fully remote — ship from Hood River area" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 7-hour Seattle round trip costs you", fpc: "Best price guarantee — no travel expense" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/portland" },
    { name: "Emergency passport help near The Dalles", route: "/oregon" },
    { name: "Fast passport renewal in Gresham", route: "/gresham" },
    { name: "Rush passport services in Troutdale", route: "/troutdale" },
    { name: "Expedited passports for Oregon travelers", route: "/oregon" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from the Hood River UPS Store or The Dalles FedEx — both open evenings. No visit to our processing facility required for most services.",
  fpcOfficeDistance: "Serving Hood River and the entire Columbia River Gorge corridor remotely",
  serviceAreaIntro: "Whether you are in Hood River, Cascade Locks, White Salmon, Bingen, Stevenson, Mosier, or anywhere in the Columbia River Gorge corridor — Oregon or Washington side — Fast Passport Center makes emergency and expedited passport processing simple.",
  nearbyCitiesIntro: "We serve all Hood River County and Columbia Gorge zip codes on both the Oregon and Washington sides of the river. Ship overnight from Hood River or The Dalles and your documents arrive at our processing center the next morning.",
  howItWorksIntro: "Getting an emergency or expedited passport from Hood River is simpler than the drive to Seattle. Most Gorge clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays and rejections for Hood River and Columbia Gorge passport applicants. We review your documents before submission to prevent them.",
  testimonialsIntro: "Real reviews from Hood River, White Salmon, and Columbia River Gorge travelers who needed their passports fast.",
  faqIntro: "The most common questions Hood River and Columbia River Gorge residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for Hood River and Columbia Gorge Residents",
    intro: "Hood River's identity is built around wind, water, and adventure — and that travel culture creates distinctive passport urgency patterns. From kite-surfing trips to Baja to Canadian ski tours and international wine tourism, here's when passport needs become urgent in the Gorge.",
    cards: [
      {
        icon: "ri-riding-line",
        title: "Wind & Water Sports — Baja, British Columbia & International Destinations",
        subtitle: "Hood River's world-class wind sports culture drives spontaneous international travel",
        description: "Hood River is one of the world's premier windsurfing and kiteboarding destinations — and that culture extends internationally. Local wind athletes book spontaneous trips to Baja California, Maui, the Canary Islands, and Tarifa Spain when wind conditions align. These bookings are often last-minute — sometimes 1–2 weeks out — and frequently coincide with recently-discovered expired passports. The same pattern applies to Hood River's kayaking, mountain biking, and outdoor guide community.",
        tip: "If you're a wind sports enthusiast in Hood River, keep your passport renewed proactively — destination wind forecasts don't wait for passport processing times. Treat your passport renewal like equipment maintenance: do it before it becomes a crisis.",
      },
      {
        icon: "ri-cup-line",
        title: "Hood River Wine Industry & International Wine Tourism",
        subtitle: "Oregon wine tourism creates professional and leisure travel to France, Italy, Germany",
        description: "Hood River Valley sits at the northern edge of Oregon wine country. The region's winemakers, sommeliers, and wine tourism professionals travel internationally — to Burgundy, the Rhône Valley, Tuscany, and Mosel — for vineyard partnerships, education, and supplier relationships. Wine tourism visitors from Portland and Vancouver also use Hood River as a base for longer international trips. Professional wine travel tends to involve complex itineraries with fixed departure dates.",
        tip: "Wine trade professionals: check every passport in your party before booking international vineyard visits. Burgundy harvest visits in September are among the most date-fixed travel experiences — there's no 'shift the trip' if your passport is expired.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "PDX International Departures — 60 Miles West",
        subtitle: "Portland International Airport serves Hood River's international travelers",
        description: "Portland International Airport is the departure point for Hood River international travel, about 60 miles west via I-84. PDX offers direct service to Amsterdam, London, Reykjavik, Tokyo, Vancouver BC, and Mexico City. Hood River-area travelers occasionally use SEA (195 miles north) for Alaska and British Columbia. Both airports require a valid U.S. passport for international departures. Peak season at PDX runs May–August, which coincides exactly with Hood River's peak wind sports season.",
        tip: "Plan extra travel time from Hood River to PDX during summer — I-84 through the Gorge sees recreational traffic delays. And confirm your passport is valid for six months beyond your return date before booking.",
      },
      {
        icon: "ri-landscape-line",
        title: "Canadian Recreation & Ski Travel",
        subtitle: "British Columbia ski resorts, Whistler, and Vancouver — popular with Hood River outdoor community",
        description: "Hood River's ski and outdoor community frequently crosses into British Columbia for Whistler skiing, Kootenay backcountry access, and Vancouver short breaks. A valid U.S. passport is required for any air travel to Canada, and strongly recommended for land crossings. Oregon residents cannot get enhanced driver licenses (a Washington-only option), so a passport is essential for the Hood River community crossing into Canada.",
        tip: "Oregon residents cannot get enhanced driver licenses — only Washington state offers them. For any Canadian air travel or cruise, a U.S. passport is the only option for Hood River residents.",
      },
    ],
    callout: {
      icon: "ri-compass-line",
      title: "The Columbia Gorge International Outdoor Sports Community",
      description: "Hood River's outdoor sports culture attracts international athletes, instructors, and adventure travelers who live in the Gorge for the wind and water — but travel globally for competitions, sponsorships, and seasonal pursuits. These residents often have complex passport situations (frequent travel, near-expiry, name changes after marriage) and need fast, professional help. Fast Passport Center handles expedited passport processing for the Hood River outdoor sports professional community.",
    },
  },
  destinations: {
    title: "Where Hood River Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Hood River and Columbia Gorge clients. Hood River's outdoor adventure culture and wine industry create distinctive international travel urgency that differs from most Oregon cities.",
    stats: [
      { stat: "60 mi", label: "to PDX — Portland International Airport from Hood River via I-84" },
      { stat: "1–2 Wks", label: "typical advance notice when Gorge outdoor athletes book international wind trips" },
      { stat: "Apr–Sep", label: "peak season when Hood River international travel and passport urgency collide" },
      { stat: "24 Hrs", label: "fastest emergency passport processing for eligible Hood River residents" },
    ],
    destinations: [
      { name: "Baja California, Mexico", context: "Kiteboarding and windsurfing trips to La Ventana, Los Barriles, and the Sea of Cortez — Hood River's most popular international wind sports destination", urgency: "Wind forecasts drive 1–2 week spontaneous trip bookings; passport expiration often discovered during flight search", icon: "ri-windy-line" },
      { name: "British Columbia, Canada", context: "Whistler and Blackcomb skiing, Kootenay backcountry snowboarding, Vancouver city breaks, Harrison Hot Springs", urgency: "Ski season bookings happen in November–December; passport issues discovered during holiday planning rush", icon: "ri-map-pin-2-line" },
      { name: "France — Burgundy & Rhône Valley", context: "Hood River wine industry travel to French vineyards for harvest visits, wine education, and supplier partnerships", urgency: "Harvest visits in September have fixed dates; wine professionals discover passport issues in August when booking", icon: "ri-map-pin-2-line" },
      { name: "Spain — Tarifa & Canary Islands", context: "World-class windsurfing and kiteboarding destinations popular with Hood River's professional wind sports athletes", urgency: "Competition schedules and wind season bookings come with 2–4 week notice for Gorge athletes", icon: "ri-map-pin-2-line" },
      { name: "Japan & South Korea", context: "Teaching English abroad programs popular with Hood River young adults; snow travel and cultural trips; Portland metro tech connections", urgency: "Teaching programs require passport submission months before departure — expiration discovered at application time", icon: "ri-map-pin-2-line" },
      { name: "Costa Rica & Central America", context: "Eco-adventure travel popular with Gorge outdoor community — surf, zip-line, white-water rafting, and wildlife tours", urgency: "Adventure travel bookings are often 3–5 weeks out for active Hood River residents", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout the Columbia Gorge corridor and greater Oregon with the same expedited passport expertise.",
    crossLinks: [
      { name: "Sandy", route: "/sandy" },
      { name: "Troutdale", route: "/troutdale" },
      { name: "Gresham", route: "/gresham" },
    ],
  },
};